import { useState, useEffect } from 'react';
import { useAlerts, useUpdateAlert } from '../../hooks/useAlerts';
import { generateAlerts, getAlertScanStatus, AlertGenerationDiagnostics, AlertScanStatus } from '../../services/api';
import AlertCard from './AlertCard';
import HelpTooltip from '../common/HelpTooltip';
import { AlertType, AlertSeverity, AlertStatus } from '../../types';
import { Filter, RefreshCw, Info, X, Clock, BellRing } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

export default function AlertsPage() {
  const [showFilters, setShowFilters] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [diagnostics, setDiagnostics] = useState<AlertGenerationDiagnostics | null>(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [scanStatus, setScanStatus] = useState<AlertScanStatus | null>(null);

  // Load scan status on mount
  useEffect(() => {
    getAlertScanStatus()
      .then(setScanStatus)
      .catch(err => console.error('Failed to get scan status:', err));
  }, []);

  const {
    alerts,
    total,
    loading,
    error,
    filters,
    setFilters,
    refetch,
  } = useAlerts({ status: 'active' });

  const { updateAlert, loading: updating } = useUpdateAlert();

  const handleSeverityFilter = (severity: AlertSeverity | '') => {
    setFilters({
      ...filters,
      severity: severity || undefined,
      page: 1,
    });
  };

  const handleTypeFilter = (alertType: AlertType | '') => {
    setFilters({
      ...filters,
      alertType: alertType || undefined,
      page: 1,
    });
  };

  const handleStatusFilter = (status: AlertStatus | '') => {
    setFilters({
      ...filters,
      status: status || undefined,
      page: 1,
    });
  };

  const handleAcknowledge = async (id: string) => {
    try {
      await updateAlert({ id, data: { status: 'acknowledged' } });
      toast.success('Alert acknowledged');
      refetch();
    } catch {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleResolve = async (id: string) => {
    try {
      await updateAlert({ id, data: { status: 'resolved' } });
      toast.success('Alert resolved');
      refetch();
    } catch {
      toast.error('Failed to resolve alert');
    }
  };

  const handleSnooze = async (id: string, until: string) => {
    try {
      await updateAlert({ id, data: { status: 'snoozed', snoozedUntil: until } });
      toast.success('Alert snoozed');
      refetch();
    } catch {
      toast.error('Failed to snooze alert');
    }
  };

  const clearFilters = () => {
    setFilters({ status: 'active' });
  };

  const handleGenerateAlerts = async () => {
    setGenerating(true);
    try {
      const result = await generateAlerts();
      if (result.diagnostics) {
        setDiagnostics(result.diagnostics);
        setShowDiagnostics(true);
      }
      if (result.alertsCreated > 0) {
        toast.success(`Generated ${result.alertsCreated} new alerts`);
        refetch();
      } else {
        toast.success('No new alerts to generate - check diagnostics for details');
      }
      // Refresh scan status
      const newStatus = await getAlertScanStatus();
      setScanStatus(newStatus);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to generate alerts');
    } finally {
      setGenerating(false);
    }
  };

  // Group alerts by severity
  const criticalAlerts = alerts.filter((a) => a.severity === 'critical');
  const warningAlerts = alerts.filter((a) => a.severity === 'warning');
  const infoAlerts = alerts.filter((a) => a.severity === 'info');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-foreground">
              Alerts
            </h1>
            <HelpTooltip
              text="Alerts notify you of important events: upcoming renewals, payment issues, low license utilization, and migration opportunities. Critical alerts need immediate attention. Acknowledge alerts you're working on, or resolve them when done."
              
            />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Monitor and manage licensing alerts
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center gap-4">
          {scanStatus && (
            <div className="text-right text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                <span>
                  Last scan: {scanStatus.lastScanTime
                    ? formatDistanceToNow(new Date(scanStatus.lastScanTime), { addSuffix: true })
                    : 'Never'}
                </span>
              </div>
              <div className="text-muted-foreground">
                Auto-scans daily at 7 AM UTC
              </div>
            </div>
          )}
          <div className="flex items-center gap-1">
            <button
              onClick={handleGenerateAlerts}
              disabled={generating}
              className="btn-secondary"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Scanning...' : 'Scan Now'}
            </button>
            <HelpTooltip
              text="Manually trigger an alert scan. Checks all subscriptions for renewal dates, payment issues, low utilization, and other conditions that need attention."
              size="sm"
            />
          </div>
        </div>
      </div>

      {/* Diagnostics Panel */}
      {showDiagnostics && diagnostics && (
        <div className="card p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
              <h3 className="font-medium text-blue-900 dark:text-blue-100">Alert Generation Diagnostics</h3>
            </div>
            <button
              onClick={() => setShowDiagnostics(false)}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-blue-600 dark:text-blue-400">Subscriptions with renewal date:</span>
              <span className="ml-2 font-medium text-blue-900 dark:text-blue-100">{diagnostics.subscriptionsWithRenewalDate}</span>
            </div>
            <div>
              <span className="text-blue-600 dark:text-blue-400">Without renewal date:</span>
              <span className="ml-2 font-medium text-orange-600 dark:text-orange-400">{diagnostics.subscriptionsWithoutRenewalDate}</span>
            </div>
            <div>
              <span className="text-blue-600 dark:text-blue-400">Renewals next 30 days:</span>
              <span className="ml-2 font-medium text-blue-900 dark:text-blue-100">{diagnostics.renewalsNext30Days}</span>
            </div>
            <div>
              <span className="text-blue-600 dark:text-blue-400">Renewals next 60 days:</span>
              <span className="ml-2 font-medium text-blue-900 dark:text-blue-100">{diagnostics.renewalsNext60Days}</span>
            </div>
            <div>
              <span className="text-blue-600 dark:text-blue-400">Flagged payment clients:</span>
              <span className={`ml-2 font-medium ${diagnostics.flaggedPaymentClients > 0 ? 'text-red-600 dark:text-red-400' : 'text-blue-900 dark:text-blue-100'}`}>
                {diagnostics.flaggedPaymentClients}
              </span>
            </div>
            <div>
              <span className="text-blue-600 dark:text-blue-400">At-risk clients:</span>
              <span className={`ml-2 font-medium ${diagnostics.atRiskClients > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-blue-900 dark:text-blue-100'}`}>
                {diagnostics.atRiskClients}
              </span>
            </div>
            <div>
              <span className="text-blue-600 dark:text-blue-400">With utilization data:</span>
              <span className="ml-2 font-medium text-blue-900 dark:text-blue-100">{diagnostics.subscriptionsWithUtilizationData}</span>
            </div>
          </div>
          {diagnostics.sampleSubscriptions.length > 0 && (
            <div className="mt-4 pt-3 border-t border-blue-200 dark:border-blue-700">
              <p className="text-xs text-blue-600 dark:text-blue-400 mb-2">Sample subscriptions:</p>
              <div className="space-y-1">
                {diagnostics.sampleSubscriptions.map((sub, idx) => (
                  <div key={idx} className="text-xs text-blue-800 dark:text-blue-200">
                    {sub.clientName} - {sub.productName} (Renewal: {sub.renewalDate})
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="mt-3 text-xs text-blue-600 dark:text-blue-400">
            Alerts are generated for: renewals within 60 days, flagged payments, at-risk clients, low utilization, and migration opportunities.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-muted-foreground">
              {total} alerts
            </span>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary ${showFilters ? 'bg-muted' : ''}`}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <label className="label mb-0">Status</label>
                  <HelpTooltip
                    text="Active: Needs attention. Acknowledged: You're working on it. Resolved: Issue fixed. Snoozed: Temporarily hidden."
                    size="sm"
                  />
                </div>
                <select
                  value={filters.status || ''}
                  onChange={(e) => handleStatusFilter(e.target.value as AlertStatus | '')}
                  className="input"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="acknowledged">Acknowledged</option>
                  <option value="resolved">Resolved</option>
                  <option value="snoozed">Snoozed</option>
                </select>
              </div>
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <label className="label mb-0">Severity</label>
                  <HelpTooltip
                    text="Critical: Immediate action needed (e.g., renewal in 7 days with payment issue). Warning: Attention needed soon. Info: Good to know but not urgent."
                    size="sm"
                  />
                </div>
                <select
                  value={filters.severity || ''}
                  onChange={(e) => handleSeverityFilter(e.target.value as AlertSeverity | '')}
                  className="input"
                >
                  <option value="">All Severities</option>
                  <option value="critical">Critical</option>
                  <option value="warning">Warning</option>
                  <option value="info">Info</option>
                </select>
              </div>
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <label className="label mb-0">Type</label>
                  <HelpTooltip
                    text="Renewal: Subscription renewing soon. Payment: Client has payment issues. Utilization: Low license usage. Cancellation: Pending cancellation. Migration: Opportunity to move direct licenses to Pax8."
                    size="sm"
                  />
                </div>
                <select
                  value={filters.alertType || ''}
                  onChange={(e) => handleTypeFilter(e.target.value as AlertType | '')}
                  className="input"
                >
                  <option value="">All Types</option>
                  <option value="renewal">Renewal</option>
                  <option value="payment">Payment</option>
                  <option value="utilization">Utilization</option>
                  <option value="cancellation">Cancellation</option>
                  <option value="migration">Migration</option>
                </select>
              </div>
              <div className="flex items-end">
                <button onClick={clearFilters} className="btn-secondary w-full">
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="card p-4 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : alerts.length === 0 ? (
        <div className="card p-12 text-center">
          <BellRing className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground">
            No alerts found
          </h3>
          <p className="text-muted-foreground mt-2">
            {filters.status === 'active'
              ? 'Great! You have no active alerts.'
              : 'No alerts match your current filters.'}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Critical Alerts */}
          {criticalAlerts.length > 0 && (
            <div>
              <h2 className="text-lg font-medium text-red-600 dark:text-red-400 mb-4">
                Critical ({criticalAlerts.length})
              </h2>
              <div className="space-y-4">
                {criticalAlerts.map((alert) => (
                  <AlertCard
                    key={alert.id}
                    alert={alert}
                    onAcknowledge={handleAcknowledge}
                    onResolve={handleResolve}
                    onSnooze={handleSnooze}
                    loading={updating}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Warning Alerts */}
          {warningAlerts.length > 0 && (
            <div>
              <h2 className="text-lg font-medium text-orange-600 dark:text-orange-400 mb-4">
                Warnings ({warningAlerts.length})
              </h2>
              <div className="space-y-4">
                {warningAlerts.map((alert) => (
                  <AlertCard
                    key={alert.id}
                    alert={alert}
                    onAcknowledge={handleAcknowledge}
                    onResolve={handleResolve}
                    onSnooze={handleSnooze}
                    loading={updating}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Info Alerts */}
          {infoAlerts.length > 0 && (
            <div>
              <h2 className="text-lg font-medium text-blue-600 dark:text-blue-400 mb-4">
                Info ({infoAlerts.length})
              </h2>
              <div className="space-y-4">
                {infoAlerts.map((alert) => (
                  <AlertCard
                    key={alert.id}
                    alert={alert}
                    onAcknowledge={handleAcknowledge}
                    onResolve={handleResolve}
                    onSnooze={handleSnooze}
                    loading={updating}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
